// AuthContext.tsx
import { createContext, useContext, useState, ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { setLogoutHandler } from "src/api/axiosInstance";

export interface User {
  idUsuario: number;
  nombre: string;
  apellido: string;
  correo: string;
  membresia: number;
  esResponsable: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, userData: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  });

  const inactivityTimeout = useRef<NodeJS.Timeout | null>(null);

  // --- LOGIN ---
  const login = (newToken: string, userData: User) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(userData));

    setToken(newToken);
    setUser(userData);

    navigate("/overview", { replace: true });
  };

  // --- LOGOUT ---
  const logout = () => {
    clearInactivityTimer();

    localStorage.removeItem("token");
    localStorage.removeItem("user");

    setToken(null);
    setUser(null);

    navigate("/login", { replace: true });
  };

  // --- INACTIVITY TIMER ---
  const resetInactivityTimer = () => {
    if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
    inactivityTimeout.current = setTimeout(() => {
      logout();
      alert("Sesión cerrada por inactividad ⏰");
    }, 1800_000); // 30 minutos
  };

  const clearInactivityTimer = () => {
    if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
  };

  // Registrar logoutHandler en Axios
  useEffect(() => {
    setLogoutHandler(logout);
  }, []);

  // --- Detectar actividad SOLO si hay token ---
  useEffect(() => {
    const events = ["mousemove", "keydown", "click"];
    const reset = () => resetInactivityTimer();

    if (token) {
      // Usuario logueado → activar listeners
      events.forEach((e) => window.addEventListener(e, reset));
      resetInactivityTimer();
    } else {
      // Usuario NO logueado → eliminar listeners y timer
      events.forEach((e) => window.removeEventListener(e, reset));
      clearInactivityTimer();
    }

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de un AuthProvider");
  return ctx;
};
